import styled, { css } from 'styled-components'

import { Box } from '../../shared_components/UI/Box'

import { Link } from 'react-router-dom'
import { defaultTransition } from '../../theme'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useScrollPosition } from '../../hooks/useScrollPosition'
import { ThemedButton } from '../../shared_components/ThemedButton'
import { useClick } from '@floating-ui/react'

export const DropTitle = styled.h1`
  font-weight: 1000;
  font-size: 72px;
  margin: 0;
  line-height: 1;
  font-family: Space Grotesk;
  letter-spacing: -0.04em;
  font-feature-settings:
    'ordn' on,
    'dnom' on,
    'ss01' on;
  color: ${(props) => props.theme.semantics.zap.surface.primary};
  @media screen and (max-width: 767px) {
    font-size: 40px;
    line-height: 45px;
  }
`

export const OuterWrapper = styled(Box)`
  z-index: 10;
  position: relative;
`

export const TopWrapper = styled(Box)`
  z-index: 10;
  flex: 0;
`

export const PageTogglerContainer = styled(Box)<{
  isFixed?: boolean
  arrowsVisible?: boolean
}>`
  width: 100%;
  z-index: 9;
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 44px;
  border: 1px solid var(--stroke-tertiary, #191919);
  padding: var(--units-4, 4px);

  @media screen and (max-width: 768px) {
    justify-content: ${({ arrowsVisible }) =>
      arrowsVisible ? 'flex-start' : 'space-between'};
  }

  @media screen and (max-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    max-width: 428px;
  }

  @media screen and (max-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    max-width: calc(100vw - calc(var(--Index-Grids-Margins, 16px) * 2) - 48px);
    overflow: hidden;
  }

  ${defaultTransition}
  ${({ isFixed }) =>
    isFixed &&
    css`
      max-width: 1400px;
      position: fixed;
      top: -80px;
      z-index: 10000;
      @media screen and (max-width: 1400px) {
        max-width: -webkit-fill-available;
        margin-right: 4rem;
      }
    `}
`

const JerkPreventor = styled.div<{ isFixed?: boolean }>`
  height: ${({ isFixed }) => (isFixed ? 156 : 0)}px;
`

export function PageToggler({
  children,
  isFixed,
  setIsFixed,
  arrowsVisible = true,
}: {
  children: ReactNode[]
  isFixed?: boolean
  setIsFixed?: (isFixed: boolean) => void
  marginTop?: number
  arrowsVisible?: boolean
}) {
  const [hasScrolledContainerForward, setHasScrolledContainerForward] =
    useState(false)
  const pageToggleRef = useRef<HTMLDivElement>(null)
  const jerkPreventorRef = useRef<HTMLDivElement>(null)
  const { scrollY } = useScrollPosition()

  useEffect(() => {
    if (!pageToggleRef?.current) return
    if (!jerkPreventorRef?.current) return
    const topPosition = (
      isFixed ? jerkPreventorRef : pageToggleRef
    ).current.getBoundingClientRect().top

    if (setIsFixed && !isFixed && topPosition < 80) {
      setIsFixed(true)
      return
    }
    if (setIsFixed && isFixed && topPosition >= -40) {
      setIsFixed(false)
      return
    }
  }, [scrollY, pageToggleRef, jerkPreventorRef, isFixed, setIsFixed])

  const scrollButtonBoxToPosition = useCallback(() => {
    if (!pageToggleRef?.current) return
    pageToggleRef.current.scrollTo({
      left: hasScrolledContainerForward ? 0 : 42069,
      behavior: 'smooth',
    })
    setHasScrolledContainerForward(!hasScrolledContainerForward)
  }, [pageToggleRef, hasScrolledContainerForward])

  return (
    <PageTogglerWrapper ac g={8}>
      <PageTogglerContainer ref={pageToggleRef} id="page-toggler">
        {children}
      </PageTogglerContainer>
      <JerkPreventor ref={jerkPreventorRef} isFixed={isFixed} />
      {arrowsVisible && (
        <ThemedButton
          w="40px"
          className={`arrow-button ${hasScrolledContainerForward ? 'inverted' : ''}`}
          onClick={scrollButtonBoxToPosition}
          $styleType="tertiary"
        >
          <img src="/img/icons/arrow-right.svg" />
        </ThemedButton>
      )}
    </PageTogglerWrapper>
  )
}

const PageTogglerWrapper = styled(Box)`
  .arrow-button {
    display: none;
  }
  @media screen and (max-width: 480px) {
    .arrow-button {
      display: flex;
    }
  }
  .arrow-button {
    max-width: 40px;
    max-height: 40px;
    img {
      min-width: 18px;
      ${defaultTransition}
    }
  }
  .arrow-button.inverted {
    img {
      transform: rotate(180deg);
    }
  }
`

export const UnstyledButton = styled.button<{ softerFilter?: boolean }>`
  border: none;
  outline: none;
  background: none;
`

type PageLinkProps = {
  $isActive: boolean
  $isFirst?: boolean
}

export const PageLink = styled(Link)<PageLinkProps>`
`

export const ChaptersListBox = styled(Box)`
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  overflow: hidden;
  height: 56px;
`

export const ChapterBox = styled(Box)<{ isActive: boolean; disabled: boolean }>`
  height: 60px;
  padding: 12px 16px;
  background: ${({ isActive }) => `rgba(3, 11, 17, ${isActive ? 0.15 : 0.65})`};
  backdrop-filter: blur(20px);

  ${defaultTransition};
  cursor: pointer;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
  :hover {
    background: ${({ isActive }) => `rgba(3, 11, 17, ${isActive ? 0.3 : 0.9})`};
  }

  .chapter-cont {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }

  .chapter-index {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-size: 12px;
    font-weight: 400;
    letter-spacing: -0.36px;
    line-height: normal;
    text-transform: capitalize;
  }
  .chapter-name {
    color: white;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.72px;
    line-height: normal;
  }
`

export const ClosedChapterLabel = styled.span`
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.36px;
  line-height: normal;
  text-transform: uppercase;

  @media screen and (max-width: 768px) {
    display: none;
  }
`

export const TitleBox = styled(Box)`
  order: 1;
  p {
    max-width: 80%;
  }

  @media screen and (max-width: 1200px) {
    min-width: 100%;
  }
`

export const LevelBox = styled(Box)`
  order: 3;
  flex: unset;
`

export const CountdownRewardBox = styled(Box)`
  align-items: flex-end;
  justify-content: flex-end;
  gap: 20px;
  order: 2;

  @media screen and (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin-top: 40px;
  }
`

export const TitleCountdownBox = styled(Box)`
  flex-wrap: wrap;
  @media screen and (max-width: 1200px) {
    flex-direction: column;
    ${LevelBox} {
      order: 2;
    }
    ${CountdownRewardBox} {
      order: 3;
    }
  }
`

export const CountdownBox = styled(Box)`
  align-items: flex-end;
  gap: 14px;

  @media screen and (max-width: 1200px) {
    align-items: center;
  }

  .chapter-index-countdown {
    color: var(--White, #fff);
    text-align: right;
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 18.025px; /* 150% */
    letter-spacing: 3.605px;
    text-transform: uppercase;
  }
`

export const HeaderBackground = styled.div<{
  backgroundUrl: string
  mobileBackgroundUrl?: string
  height: string
  mobileH: string
  width?: string
  top?: number
  backgroundPosition?: string
  wideScreenBackgroundSize?: string
  backgroundGradient?: string
}>`
  background-image: ${({ backgroundGradient }) =>
    backgroundGradient ??
    `radial-gradient(
    54.86% 54.7% at 61.16% 39.14%,
    #ffffff00 0%,
    #04111b 100%
  )`},
    url('${({ backgroundUrl }) => backgroundUrl}');
  background-size: cover;
  background-position: ${({ backgroundPosition }) =>
    backgroundPosition ?? 'center'};
  background-repeat: no-repeat;

  height: ${({ height }) => height};
  @media (max-width: 768px) {
    background-image: ${({ backgroundGradient }) =>
      backgroundGradient ??
      `radial-gradient(
    54.86% 54.7% at 61.16% 39.14%,
    #ffffff00 0%,
    #04111b 100%
  )`},
      url('${({ backgroundUrl, mobileBackgroundUrl }) =>
        mobileBackgroundUrl ?? backgroundUrl}');
    height: ${({ mobileH }) => mobileH};
  }

  position: absolute;
  top: -${({ top }) => top ?? 0}px;
  left: 0;
  right: 0;
  overflow: hidden;
  width: 100%
  z-index: 1;
`

const LockIconCont = styled.div`
  display: flex;
  width: 28px;
  height: 28px;
  padding: 4px;
  justify-content: center;
  align-items: center;

  background: rgba(255, 255, 255, 0.04);
`

export const LockIcon = () => {
  return (
    <LockIconCont>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="21"
        height="21"
        viewBox="0 0 21 21"
        fill="none"
      >
        <path
          opacity="0.5"
          d="M14.6668 9.16146V7.49479C14.6668 5.19361 12.8013 3.32812 10.5002 3.32812C8.19898 3.32812 6.3335 5.1936 6.3335 7.49479V9.16146M10.5002 12.9115V14.5781M7.8335 18.3281H13.1668C14.567 18.3281 15.267 18.3281 15.8018 18.0556C16.2722 17.816 16.6547 17.4335 16.8943 16.9631C17.1668 16.4283 17.1668 15.7283 17.1668 14.3281V13.1615C17.1668 11.7613 17.1668 11.0613 16.8943 10.5265C16.6547 10.0561 16.2722 9.67362 15.8018 9.43394C15.267 9.16146 14.567 9.16146 13.1668 9.16146H7.8335C6.43336 9.16146 5.7333 9.16146 5.19852 9.43394C4.72811 9.67362 4.34566 10.0561 4.10598 10.5265C3.8335 11.0613 3.8335 11.7613 3.8335 13.1615V14.3281C3.8335 15.7283 3.8335 16.4283 4.10598 16.9631C4.34566 17.4335 4.72811 17.816 5.19852 18.0556C5.7333 18.3281 6.43336 18.3281 7.8335 18.3281Z"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </LockIconCont>
  )
}

export const RewardsCont = styled(Box)`
  flex: unset;
  width: auto;
  justify-content: flex-end;
  gap: 8px;

  @media screen and (max-width: 1200px) {
    flex: 1;
    width: 100%;
  }
`

export const RewardBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex: unset;
  width: 142px;
  max-width: 100%;

  @media screen and (max-width: 1200px) {
    width: 100%;
  }

  .reward-amount {
    display: flex;
    padding: 12px 0px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8;
    align-self: stretch;

    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(3, 11, 17, 0.4);
    backdrop-filter: blur(18.871339797973633px);

    .reward-amount-worth {
      color: #fff;
      text-align: center;
      leading-trim: both;
      text-edge: cap;
      font-feature-settings: 'dlig' on;
      font-family: 'Space Grotesk';
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    }
    .reward-amount-est {
      color: #fff;
      text-align: center;
      leading-trim: both;
      text-edge: cap;
      font-family: 'Space Grotesk';
      font-size: 10px;
      font-style: normal;
      font-weight: 400;
      line-height: 16.92px; /* 169.2% */
    }
  }

  .reward-img {
    background: rgba(3, 11, 17, 0.01);
    backdrop-filter: blur(21.179744720458984px);
    max-width: 118px;
    padding: 24px 0;
  }

  .reward-ticker {
    padding: 12px 0;

    border-top: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(3, 11, 17, 0.4);
    backdrop-filter: blur(18.871339797973633px);

    > span {
      text-align: center;
      leading-trim: both;
      text-edge: cap;
      font-feature-settings: 'dlig' on;
      font-family: 'Space Grotesk';
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      text-transform: uppercase;

      background: linear-gradient(90deg, #ffebd2 0%, #fff 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
`

// position: ${({ position }) => position ?? 'relative'};
export const ForceFullWidthMobile = styled.div`
  width: 100%;

  @media screen and (max-width: 768px) {
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
  }
`
